/**
 * Collection layer reads. Awards are DB triggers (see migration 024) —
 * the client only ever SELECTs (RLS-gated) and renders.
 */
import { supabase } from '../lib/supabase';
import { normalizeAddress } from '../lib/shop-address';
import { weeklyStreak, repairableWeek, dateForWeekKey } from '../lib/streak';

export interface CatalogItem {
  id: string; slug: string; name: string; description: string;
  rarity: 'common' | 'uncommon' | 'rare' | 'legendary';
  icon: string; shop_id: string | null; sponsor_id: string | null;
}
export interface OwnedCollectible { collectible_id: string; print_number: number; earned_at: string; }
export interface CollectionEntry extends CatalogItem { owned: boolean; print_number?: number; earned_at?: string; }
export interface SecretBadgeRow { slug: string; name: string; description: string; hint: string; }

const RARITY_ORDER: Record<CatalogItem['rarity'], number> = { legendary: 3, rare: 2, uncommon: 1, common: 0 };

export async function getCatalog(shopId: string | null): Promise<CatalogItem[]> {
  const isUuid = (v: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);
  if (shopId && !isUuid(shopId)) shopId = null;
  let q = supabase.from('collectibles_catalog')
    .select('id, slug, name, description, rarity, icon, shop_id, sponsor_id')
    .eq('active', true);
  q = shopId ? q.or(`shop_id.is.null,shop_id.eq.${shopId}`) : q.is('shop_id', null);
  const { data, error } = await q;
  if (error || !data) return [];
  return data as CatalogItem[];
}

/** Catalog rows for specific ids — NO active/shop filter. Owned items must
 * always be renderable, even if their shop deactivated them or the customer
 * is currently viewing a different shop. */
export async function getCatalogByIds(ids: string[]): Promise<CatalogItem[]> {
  if (ids.length === 0) return [];
  const { data, error } = await supabase.from('collectibles_catalog')
    .select('id, slug, name, description, rarity, icon, shop_id, sponsor_id')
    .in('id', ids);
  if (error || !data) return [];
  return data as CatalogItem[];
}

export async function getMyCollectibles(customerAddress: string): Promise<OwnedCollectible[]> {
  const { data, error } = await supabase.from('customer_collectibles')
    .select('collectible_id, print_number, earned_at')
    .eq('customer_address', normalizeAddress(customerAddress));
  if (error || !data) return [];
  return data as OwnedCollectible[];
}

export async function getRedemptionCount(customerAddress: string): Promise<number> {
  const { count, error } = await supabase.from('redeem_requests')
    .select('*', { count: 'exact', head: true })
    .eq('customer_address', normalizeAddress(customerAddress))
    .eq('onchain_status', 'confirmed');
  if (error || count == null) return 0;
  return count;
}

export async function getSecretBadges(): Promise<SecretBadgeRow[]> {
  const { data, error } = await supabase.from('secret_badges').select('*');
  if (error || !data) return [];
  return data as SecretBadgeRow[];
}

export async function getMySecretBadges(customerAddress: string): Promise<string[]> {
  const { data, error } = await supabase.from('customer_secret_badges')
    .select('badge_slug')
    .eq('customer_address', normalizeAddress(customerAddress));
  if (error || !data) return [];
  return data.map((r: any) => r.badge_slug);
}

/** Global circulation counts per collectible ("N IN CIRCULATION" on the item
 * card). customer_collectibles is own-rows-only under RLS, so this goes
 * through the collectible_mint_counts() SECURITY DEFINER RPC (migration 025),
 * which returns ONLY aggregate counts — never any addresses or rows. */
export async function getMintCounts(): Promise<Record<string, number>> {
  const { data, error } = await supabase.rpc('collectible_mint_counts');
  if (error || !data) return {};
  const out: Record<string, number> = {};
  for (const row of data as any[]) out[row.collectible_id] = Number(row.mints) || 0;
  return out;
}

export async function getEggBeans(customerAddress: string): Promise<number> {
  const { data, error } = await supabase.from('easter_egg_claims')
    .select('beans')
    .eq('customer_address', normalizeAddress(customerAddress));
  if (error || !data) return 0;
  return data.reduce((sum, row: any) => sum + (row.beans || 0), 0);
}

/** Customer's weekly visit streak at one shop (own purchase_events via RLS). */
export async function getMyShopStreak(customerAddress: string, shopUuid: string | null): Promise<number> {
  return (await getMyStreakState(customerAddress, shopUuid)).streak;
}

export interface StreakState {
  streak: number;
  repairableWeek: string | null; // non-null → offer the streak saver
  repairSpentBeans: number;      // total spent on repairs (fun-beans subtraction)
}

/**
 * Streak + saver state in one read (041): merges streak_repairs (own rows via
 * RLS) into the purchase weeks so a bought-back week counts, and reports
 * whether the ONE repairable gap (last week) is open.
 */
export async function getMyStreakState(customerAddress: string, shopUuid: string | null): Promise<StreakState> {
  if (!shopUuid) return { streak: 0, repairableWeek: null, repairSpentBeans: 0 };
  const addr = normalizeAddress(customerAddress);
  const [{ data: events }, { data: repairs }] = await Promise.all([
    supabase.from('purchase_events')
      .select('purchased_at')
      .eq('customer_address', addr)
      .eq('shop_id', shopUuid),
    supabase.from('streak_repairs')
      .select('week_key, cost_beans')
      .eq('customer_address', addr)
      .eq('shop_id', shopUuid),
  ]);
  const dates: (string | Date)[] = [
    ...(events || []).map((r: any) => r.purchased_at),
    ...(repairs || []).map((r: any) => dateForWeekKey(r.week_key)),
  ];
  return {
    streak: weeklyStreak(dates),
    repairableWeek: repairableWeek(dates),
    repairSpentBeans: (repairs || []).reduce((sum, r: any) => sum + (r.cost_beans || 0), 0),
  };
}

/** Earned shout-a-mate bonus beans, both directions (041; own rows via RLS). */
export async function getReferralBeans(customerAddress: string): Promise<number> {
  const addr = normalizeAddress(customerAddress);
  const { data, error } = await supabase.from('referrals')
    .select('bonus_beans, status, referrer_address, referee_address')
    .or(`referrer_address.eq.${addr},referee_address.eq.${addr}`)
    .eq('status', 'earned');
  if (error || !data) return 0;
  return data.reduce((sum, row: any) => sum + (row.bonus_beans || 0), 0);
}

export interface ReferralStats {
  invited: number;  // mates who joined off my link
  earned: number;   // of those, how many hit the purchase gate
}

export async function getMyReferralStats(customerAddress: string): Promise<ReferralStats> {
  const addr = normalizeAddress(customerAddress);
  const { data, error } = await supabase.from('referrals')
    .select('status')
    .eq('referrer_address', addr);
  if (error || !data) return { invited: 0, earned: 0 };
  return {
    invited: data.length,
    earned: data.filter((r: any) => r.status === 'earned').length,
  };
}

export interface SponsorFeature {
  sponsor_name: string; logo_url: string | null; website: string | null;
  bean_name: string; tasting_notes: string[]; link_label: string;
}

/** The currently-active sponsor feature, if any. Dormant surface: returns
 * null (and the panel renders nothing) until a sponsors/sponsor_features row
 * is created by SQL when a real sponsorship is sold. */
export async function getActiveSponsorFeature(): Promise<SponsorFeature | null> {
  const nowIso = new Date().toISOString();
  const { data, error } = await supabase.from('sponsor_features')
    .select('copy, sponsors(name, logo_url, website, active)')
    .lte('starts_at', nowIso)
    .gte('ends_at', nowIso)
    .limit(1)
    .maybeSingle();
  if (error || !data) return null;
  const sponsor = (data as any).sponsors;
  if (!sponsor?.active) return null;
  const copy = (data as any).copy || {};
  return {
    sponsor_name: sponsor.name,
    logo_url: sponsor.logo_url || null,
    website: sponsor.website || null,
    bean_name: copy.bean_name || '',
    tasting_notes: Array.isArray(copy.tasting_notes) ? copy.tasting_notes.slice(0, 3) : [],
    link_label: copy.link_label || 'LEARN MORE',
  };
}

export interface AwardedCollectible {
  slug: string;
  name: string;
  rarity: string;
  icon: string | null;
  print_number: number;
}

/**
 * The collectible awarded by THIS redemption, for the reveal.
 *
 * No realtime and no polling: award_collectible_on_redeem fires in the same
 * transaction as the onchain_status='confirmed' update, and
 * customer_collectibles.redemption_id is unique per redemption — so by the
 * time that update returns, the row exists and the redeem row id is the key.
 */
export async function getCollectibleForRedemption(
  redemptionId: string,
): Promise<AwardedCollectible | null> {
  const { data, error } = await supabase
    .from('customer_collectibles')
    .select('print_number, collectibles_catalog(slug, name, rarity, icon)')
    .eq('redemption_id', redemptionId)
    .maybeSingle();

  if (error || !data) return null;
  const cat = (data as any).collectibles_catalog;
  if (!cat) return null;
  return {
    slug: cat.slug, name: cat.name, rarity: cat.rarity,
    icon: cat.icon ?? null, print_number: (data as any).print_number,
  };
}

/** Pure merge: owned-first, then rarity desc; duplicates keep highest print. */
export function mergeCollection(catalog: CatalogItem[], owned: OwnedCollectible[]): CollectionEntry[] {
  const ownedById = new Map<string, OwnedCollectible>();
  for (const o of owned) {
    const prev = ownedById.get(o.collectible_id);
    if (!prev || o.print_number > prev.print_number) ownedById.set(o.collectible_id, o);
  }
  return catalog
    .map(item => {
      const o = ownedById.get(item.id);
      return o
        ? { ...item, owned: true, print_number: o.print_number, earned_at: o.earned_at }
        : { ...item, owned: false };
    })
    .sort((a, b) =>
      Number(b.owned) - Number(a.owned) ||
      RARITY_ORDER[b.rarity] - RARITY_ORDER[a.rarity] ||
      a.name.localeCompare(b.name));
}
